import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useLocalization } from "@fluent/react";
import {
  Button,
  ContentCard,
  IconButton,
  LoadingSkeleton,
  ResponsiveFlex,
  Row,
} from "@narmi/design_system";
import { phoneFormatter, useClipboard, useNotificationContext } from "cerulean";
import Filters from "byzantine/src/filters";
import type Payee from "byzantine/src/Payee";

interface ViewPayeeProps {
  payee: Payee;
  isOpen: boolean;
  onDelete: () => void;
}

const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Row alignItems="top" gapSize="xxs">
    <Row.Item>
      <div className="fontSize--s fontColor--secondary">{label}</div>
    </Row.Item>
    <Row.Item>
      <div style={{ overflowWrap: "anywhere" }}>{children}</div>
    </Row.Item>
  </Row>
);

const CopyableValue = ({ value }: { value: string }) => {
  const { l10n } = useLocalization();
  const copiedMessage = l10n.getString("payee-drawer-account-number-copied");
  const { copy, status } = useClipboard(value, {
    successMessage: copiedMessage,
    pendingMessage: l10n.getString("payee-drawer-copy-account-number"),
    errorMessage: l10n.getString("payee-drawer-copy-failed"),
  });

  return (
    <Row alignItems="center" gapSize="xs">
      <Row.Item shrink>{value}</Row.Item>
      <Row.Item shrink>
        <IconButton
          kind="plain"
          name={status === copiedMessage ? "check" : "copy"}
          label={status}
          onClick={copy}
        />
      </Row.Item>
    </Row>
  );
};

const getAddressLines = (address: Payee["address"]) => {
  if (!address?.street_address) return [];
  const cityLine = [
    address.city,
    [address.region_code, address.postal_code].filter(Boolean).join(" "),
  ];
  return [
    [address.street_address, address.street_address_2]
      .filter(Boolean)
      .join(", "),
    cityLine.filter(Boolean).join(", "),
    address.country_code,
  ].filter(Boolean);
};

const ViewPayee = ({ payee, isOpen, onDelete }: ViewPayeeProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const [details, setDetails] = useState<Payee>();
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  useEffect(() => {
    if (!isOpen || details) return undefined;
    let isCancelled = false;
    setIsFetchingDetails(true);
    payee
      .getPayeeDetails()
      .then((fetchedPayee: Payee | undefined) => {
        if (!isCancelled && fetchedPayee) setDetails(fetchedPayee);
      })
      .catch(() => {
        if (!isCancelled) {
          sendNotification({
            type: "negative",
            text: l10n.getString("payee-drawer-details-error"),
          });
        }
      })
      .finally(() => {
        if (!isCancelled) setIsFetchingDetails(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [isOpen]);

  const shownPayee = details ?? payee;
  const name = Filters.titlecase(shownPayee.name);
  const isPerson = shownPayee.isPersonType();
  const fullAccountNumber = details?.account_number;
  const accountNumber = fullAccountNumber || payee.masked_account_number;
  const addressLines = getAddressLines(shownPayee.address);

  return (
    <ResponsiveFlex gapSize="xl">
      <Row alignItems="top">
        <Row.Item>
          <h2 style={{ overflowWrap: "anywhere" }}>{name}</h2>
          <div className="fontSize--s fontColor--secondary">
            {l10n.getString("payee-drawer-subtitle")}
          </div>
        </Row.Item>
        <Row.Item shrink>
          <Button
            kind="plain"
            startIcon="trash-2"
            onClick={onDelete}
            label={l10n.getString("button-menu-delete")}
          />
        </Row.Item>
      </Row>
      <LoadingSkeleton content="paragraph" isLoading={isFetchingDetails}>
        <ContentCard kind="bordered" paddingSize="l">
          <ResponsiveFlex gapSize="s">
            <DetailRow label={l10n.getString("payee-drawer-type-label")}>
              {isPerson
                ? l10n.getString("label-personal")
                : l10n.getString("label-business")}
            </DetailRow>
            <DetailRow label={l10n.getString("label-name")}>{name}</DetailRow>
            {shownPayee.description ? (
              <DetailRow label={l10n.getString("add-payee-description-label")}>
                {shownPayee.description}
              </DetailRow>
            ) : null}
            {!isPerson && accountNumber ? (
              <DetailRow label={l10n.getString("label-account-number")}>
                {fullAccountNumber ? (
                  <CopyableValue value={fullAccountNumber} />
                ) : (
                  accountNumber
                )}
              </DetailRow>
            ) : null}
            {isPerson && shownPayee.email ? (
              <DetailRow label={l10n.getString("label-email")}>
                {shownPayee.email}
              </DetailRow>
            ) : null}
            {shownPayee.phone ? (
              <DetailRow label={l10n.getString("label-phone-number")}>
                {phoneFormatter(shownPayee.phone)}
              </DetailRow>
            ) : null}
            {!isPerson && addressLines.length ? (
              <DetailRow label={l10n.getString("label-street-address")}>
                {addressLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </DetailRow>
            ) : null}
          </ResponsiveFlex>
        </ContentCard>
      </LoadingSkeleton>
    </ResponsiveFlex>
  );
};

export default ViewPayee;
